import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { servicePillars } from '../../data/services';
import { ApiBrackets, GearOutline, ServerRack, CloudOutline, NetworkNodes } from '../../components/Doodle/Doodle';
import { IsoServer, IsoCog, HexPrism } from '../../components/Svg3D/Svg3D';
import styles from './TechServices.module.css';

const ServiceCard = ({ service, pillarIdx, serviceIdx, isExpanded, onToggle }) => {
  const cardRef = useRef(null);
  const contentRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const Icon = service.icon;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`${styles.serviceCard} ${isVisible ? styles.visible : ''} ${isExpanded ? styles.expanded : ''}`}
      style={{ transitionDelay: `${serviceIdx * 100}ms` }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardIcon}>
          <Icon size={28} />
        </div>
        <h4 className={styles.cardTitle}>{service.title}</h4>
      </div>
      <p className={styles.cardSummary}>{service.summary}</p>
      <div
        className={styles.featuresWrapper}
        style={{
          maxHeight: isExpanded ? `${contentRef.current?.scrollHeight || 500}px` : '0px',
        }}
      >
        <ul ref={contentRef} className={styles.featuresList}>
          {service.features.map((feature, i) => (
            <li key={i} className={styles.featureItem}>
              <CheckCircle size={16} className={styles.checkIcon} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const PillarSection = ({ pillar, pillarIdx, expandedCard, setExpandedCard }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${styles.pillarSection} ${isVisible ? styles.visible : ''}`}
    >
      <div className={styles.pillarHeader}>
        <h3 className={styles.pillarName}>{pillar.name}</h3>
        <p className={styles.pillarDescription}>{pillar.description}</p>
      </div>
      <div className={styles.servicesGrid}>
        {pillar.services.map((service, serviceIdx) => {
          const cardKey = `${pillarIdx}-${serviceIdx}`;
          return (
            <ServiceCard
              key={cardKey}
              service={service}
              pillarIdx={pillarIdx}
              serviceIdx={serviceIdx}
              isExpanded={expandedCard === cardKey}
              onToggle={() =>
                setExpandedCard(expandedCard === cardKey ? null : cardKey)
              }
            />
          );
        })}
      </div>
    </div>
  );
};

const TechServices = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className={`${styles.section} grid-bg`}>
      <ApiBrackets position={{ top: '3%', left: '1%' }} size={160} theme="light" opacity={0.18} className={styles.doodle} />
      <GearOutline position={{ bottom: '5%', right: '2%' }} size={170} theme="light" opacity={0.16} className={styles.doodle} />
      <ServerRack position={{ top: '30%', right: '1%' }} size={120} theme="light" opacity={0.14} className={styles.doodle} />
      <CloudOutline position={{ bottom: '15%', left: '2%' }} size={150} theme="light" opacity={0.15} className={styles.doodle} />
      <NetworkNodes position={{ top: '60%', left: '1%' }} size={110} theme="light" opacity={0.12} className={styles.doodle} />
      <IsoServer position={{ top: '8%', right: '8%' }} size={170} opacity={0.5} />
      <IsoCog position={{ bottom: '25%', right: '6%' }} size={150} opacity={0.45} />
      <HexPrism position={{ top: '45%', left: '5%' }} size={130} opacity={0.4} />
      <div className={styles.container}>
        <div
          ref={headerRef}
          className={`${styles.header} ${headerVisible ? styles.visible : ''}`}
        >
          <span className={styles.sectionTag}>WHAT WE DO</span>
          <h2 className={styles.heading}>
            14 Service Domains. One Integrated Partner.
          </h2>
          <p className={styles.subtitle}>
            From product strategy through production operations — we cover the
            full lifecycle so you don't need five vendors.
          </p>
        </div>

        <div className={styles.pillarsContainer}>
          {servicePillars.map((pillar, pillarIdx) => (
            <PillarSection
              key={pillar.name}
              pillar={pillar}
              pillarIdx={pillarIdx}
              expandedCard={expandedCard}
              setExpandedCard={setExpandedCard}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechServices;
